import {
  Col,
  Row,
  Form,
  Alert,
  Button,
  InputGroup,
  SplitButton
} from "react-bootstrap";
import moment from "moment";
import styled from "styled-components";
import _debounce from "lodash/debounce";
import { useSelector } from "react-redux";
import {
  faPhone,
  faCheck,
  faUserClock,
  faCheckCircle,
  faXmarkCircle,
  faCalendarDays,
  faClockRotateLeft,
  faArrowRotateRight
} from "@fortawesome/free-solid-svg-icons";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  SEARCH_LEAD_BY,
  TIME_DATE_FORMAT,
  OFFER_SENT_STAGE,
  ALL_DOTS_REG_PATTERN,
  SCRIPT_WORKSPACE_PATH,
  SCRIPT_LOCAL_FILE_NAME,
  OFFER_FOLLOWUP_OUTCOMES,
  OFFER_FOLLOWUP_SEARCH_OPTIONS,
  LEAD_AGENT_TIMESTAMP_UPDATE_DELAY,
  OFFER_FOLLOWUP_NEXT_ATTEMPT_FIELDS
} from "../strings";
import Loader from "../loader/loader";
import {
  generateDateTime,
  generateScriptUrl
} from "../../services/common-service";
import {
  startOfferFollowupCall,
  releaseOfferFollowupLead,
  getNextOfferFollowupLead,
  completeOfferFollowupLead,
  getOfferFollowupLeadsBySearch,
  validateAndUpdateOfferFollowupAgentInfo
} from "../../services/api-declaration";
import {
  CustomDiv,
  CustomBadge,
  DotSeparator,
  SelectContainer,
  CustomCardHeader,
  CustomFontSizeDiv,
  ExtraInfoCustomBadge,
  AbsoluteInfoContainer,
  ControlledHeightContainer
} from "../lead-agent/lead-agent-styles";
import OfferActivities from "./offer-activities";
import PickerDialog from "../lead-agent/picker-dialog";
import PDFViewer from "../leadservice-panel/pdf-viewer";
import SpinnerButton from "../spinner-button/spinner-button";
import { getDealStageTitle } from "../leads/deal-stages-hash";
import { AgentViewActivityBtn, CustomCard } from "../common-styling";
import { showError, showSuccess } from "../../services/toast-service";
import { useAgentAssignment } from "../../context/agent-assignment-context";
import useBlockInCallNavigation from "../../hooks/useBlockInCallNavigation";
import { useTranslation } from "react-i18next";

const { CALLBACK_REQUIRED, NOT_REACHED, NOT_SUITABLE, SUITABLE } =
  OFFER_FOLLOWUP_OUTCOMES;

const OUTCOME_ACTIONS = [
  {
    outcome: NOT_REACHED,
    labelKey: "notReached",
    icon: faUserClock,
    activeVariant: "dark",
    inactiveVariant: "outline-dark"
  },
  {
    outcome: CALLBACK_REQUIRED,
    labelKey: "contactLater",
    icon: faCalendarDays,
    activeVariant: "secondary",
    inactiveVariant: "outline-secondary"
  },
  {
    outcome: SUITABLE,
    labelKey: "qualified",
    icon: faCheckCircle,
    activeVariant: "success",
    inactiveVariant: "outline-success"
  },
  {
    outcome: NOT_SUITABLE,
    labelKey: "notSuitable",
    icon: faXmarkCircle,
    activeVariant: "danger",
    inactiveVariant: "outline-danger"
  }
];

const ContactHistoryScroll = styled.div`
  max-height: 330px;
  overflow: auto;
`;

const NotesTextArea = styled(Form.Control)`
  font-size: 16px;
  border: 1px solid var(--field-border-color-regular);
  &:focus-visible {
    outline: none;
  }
`;

const ActionBlock = styled(CustomDiv)`
  min-height: 142px;
`;

const CallContactButton = styled(AgentViewActivityBtn)`
  &:disabled,
  &.disabled {
    color: #fff;
    background-color: var(--bs-success);
    border-color: var(--bs-success);
  }
`;

const OutcomeButton = styled(AgentViewActivityBtn)`
  &.is-selected {
    font-weight: 700;
    box-shadow:
      inset 0 0 0 2px rgba(255, 255, 255, 0.72),
      0 0 0 2px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }
`;

const HistoryItem = styled.div`
  position: relative;
  padding-left: 48px;
  padding-bottom: 22px;
  &:before {
    content: "";
    position: absolute;
    left: 20px;
    top: 40px;
    bottom: 0;
    width: 2px;
    background: var(--color-light-gray);
  }
  &:last-child:before {
    display: none;
  }
`;

const HistoryIcon = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 40px;
  height: 40px;
  border: 2px solid var(--color-light-gray);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-light-gray);
  background: #fff;
`;

const EMPTY_VALUE = "---";

const toOfferFollowupDecision = (outcome) => outcome.toLowerCase();

const getMinimumCallbackDateTime = () =>
  moment().add(4, "hours").format(TIME_DATE_FORMAT);

const isValidFutureDate = (value) =>
  value && moment(value).isValid() && moment(value).isAfter(moment());

const getStageDisplayName = (historyItem) => {
  if (typeof historyItem.dealStage !== "string") return EMPTY_VALUE;
  const translatedStage = getDealStageTitle(historyItem.dealStage);
  return translatedStage;
};

const getContactName = (lead) => {
  if (lead.contactName) return lead.contactName;
  const fullName = [lead.firstName, lead.lastName].filter(Boolean).join(" ");
  if (fullName) return fullName;
  return EMPTY_VALUE;
};

const getContactCloudLeadId = (entry, lead) => {
  if (entry?.contactCloudLeadId) return entry.contactCloudLeadId;
  return lead._id;
};

const formatOfferVolume = (value) => {
  if (!value) return EMPTY_VALUE;
  const normalized =
    typeof value === "number"
      ? value
      : Number(
          String(value).replace(ALL_DOTS_REG_PATTERN, "").replace(",", ".")
        );
  if (!Number.isFinite(normalized)) return value;
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(normalized);
};

const getDaysAgo = (date) => {
  if (!date || !moment(date).isValid()) return null;
  return Math.max(
    moment().startOf("day").diff(moment(date).startOf("day"), "days"),
    0
  );
};

const getOfferSentAt = (entry, lead) => {
  if (entry?.angebotVersendetAt) {
    return entry.angebotVersendetAt;
  }
  if (lead?.lastStage?.createdAt) {
    return lead.lastStage.createdAt;
  }
  const dealHistory = Array.isArray(lead?.dealHistory) ? lead.dealHistory : [];
  const offerSentHistory = [...dealHistory]
    .filter((history) => history.dealStage === OFFER_SENT_STAGE)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  return offerSentHistory?.createdAt;
};

const getContactHistory = (lead) => {
  if (!Array.isArray(lead?.dealHistory)) {
    return [];
  }
  return [...lead.dealHistory].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
};

const getLeadDetails = (entry) => {
  const lead = entry?.leadSnapshot || {};
  return {
    contactCloudLeadId: getContactCloudLeadId(entry, lead),
    createdAt: entry?.createdAt,
    companyName: lead.companyName || EMPTY_VALUE,
    tenantName: lead.tenant?.name || EMPTY_VALUE,
    contactName: getContactName(lead),
    phone: lead.phone || "",
    email: lead.email || EMPTY_VALUE,
    offerSentAt: getOfferSentAt(entry, lead),
    offerValue: formatOfferVolume(lead.offerValue),
    service: lead.service || EMPTY_VALUE
  };
};

const getNextAllowedAttemptAt = (entry) => {
  const nextAttemptField = OFFER_FOLLOWUP_NEXT_ATTEMPT_FIELDS.find((field) =>
    isValidFutureDate(entry?.[field])
  );
  if (nextAttemptField) {
    return entry[nextAttemptField];
  }
  const nextScheduledAttempt = [...(entry?.attempts || [])]
    .filter((attempt) => isValidFutureDate(attempt.nextScheduledAt))
    .sort(
      (a, b) => new Date(a.nextScheduledAt) - new Date(b.nextScheduledAt)
    )[0];
  return nextScheduledAttempt?.nextScheduledAt;
};

const OfferFollowupAgentPanelView = () => {
  const { t } = useTranslation();
  const { registerAgentAssignment } = useAgentAssignment();
  const user = useSelector((state) => state.auth.user);
  const tRef = useRef(t);
  const timer = useRef();
  const phoneNumberClickRef = useRef();
  const searchRequestId = useRef(0);
  const [entry, setEntry] = useState(null);
  const [searchType, setSearchType] = useState(SEARCH_LEAD_BY.PHONE);
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [assignedVia, setAssignedVia] = useState(SEARCH_LEAD_BY.AUTO);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isCallStarted, setIsCallStarted] = useState(false);
  const [showContactLaterDialog, setShowContactLaterDialog] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [agentNotes, setAgentNotes] = useState("");
  const [nextAttemptAfter, setNextAttemptAfter] = useState(
    getMinimumCallbackDateTime()
  );

  const leadDetails = useMemo(() => getLeadDetails(entry), [entry]);
  const offerSentDaysAgo = useMemo(
    () => getDaysAgo(leadDetails.offerSentAt),
    [leadDetails.offerSentAt]
  );
  const history = useMemo(
    () => getContactHistory(entry?.leadSnapshot),
    [entry]
  );
  const nextAllowedAttemptAt = useMemo(
    () => getNextAllowedAttemptAt(entry),
    [entry]
  );
  const isCallRestricted = isValidFutureDate(nextAllowedAttemptAt);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadNextLead = useCallback(
    async (searchParams = {}, requestedAssignedVia = SEARCH_LEAD_BY.AUTO) => {
      try {
        setIsLoading(true);
        setShowContactLaterDialog(false);
        setIsCallStarted(false);
        setOutcome(null);
        setAgentNotes("");
        setNextAttemptAfter(getMinimumCallbackDateTime());
        const response = await getNextOfferFollowupLead(searchParams);
        const responseData = response.data?.data;
        // The endpoint can return the queue entry directly or under `entry`.
        const nextEntry = responseData?.entry;
        if (nextEntry?._id) {
          setEntry(nextEntry);
          setAssignedVia(responseData?.assignedVia || requestedAssignedVia);
          if (nextEntry?.callStartedAt) {
            setIsCallStarted(true);
          }
        } else {
          setEntry(null);
          setAssignedVia(SEARCH_LEAD_BY.AUTO);
        }
      } catch (error) {
        console.log("Error in getting offer follow-up lead", error);
        showError(
          error?.response?.data?.message ||
            tRef.current("offerFollowupAgent.errors.loadLead")
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchSearchOptions = useMemo(
    () =>
      _debounce(async (value, type, requestId) => {
        if (requestId !== searchRequestId.current) return;
        try {
          const response = await getOfferFollowupLeadsBySearch({
            [type === SEARCH_LEAD_BY.PHONE ? "phone" : "email"]: value
          });
          const responseData = response.data?.data;
          setSearchOptions(responseData?.rows || responseData || []);
        } catch (error) {
          console.log("Error in searching offer follow-up leads", error);
          setSearchOptions([]);
          showError(
            error?.response?.data?.message ||
              t("offerFollowupAgent.errors.searchLeads")
          );
        } finally {
          setIsSearching(false);
        }
      }, 500),
    [t]
  );

  const handleSearchInputChange = (value, action) => {
    if (action !== "input-change" || isCallStarted) return;
    const isValidPhoneSearch = /^[+\d\s()./-]+$/.test(value);
    setSearchValue(value);
    setSearchOptions([]);
    searchRequestId.current += 1;
    fetchSearchOptions.cancel();
    const normalizedValue = value.trim();
    if (
      !normalizedValue ||
      (searchType === SEARCH_LEAD_BY.PHONE && !isValidPhoneSearch)
    ) {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    fetchSearchOptions(normalizedValue, searchType, searchRequestId.current);
  };

  const handleStartCall = async () => {
    if (isSaving || !leadDetails.phone) return;
    if (isCallRestricted) {
      showError(
        t("offerFollowupAgent.errors.callNotAllowedYet", {
          date: generateDateTime(nextAllowedAttemptAt)
        })
      );
      return;
    }
    try {
      setIsSaving(true);
      await startOfferFollowupCall(entry._id, { assignedVia });
      setIsCallStarted(true);
      phoneNumberClickRef.current?.click();
    } catch (error) {
      console.log("Error in starting offer follow-up call", error);
      showError(
        error?.response?.data?.message ||
          t("offerFollowupAgent.errors.startCall")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleContactLaterSelected = async ({ contactLaterVal }) => {
    setNextAttemptAfter(moment(contactLaterVal).format(TIME_DATE_FORMAT));
    setOutcome(CALLBACK_REQUIRED);
  };

  const handleComplete = async () => {
    if (!outcome) {
      showError(t("offerFollowupAgent.errors.invalidDecision"));
      return;
    }

    if (agentNotes.trim().length < 20) {
      showError(t("offerFollowupAgent.errors.notesLength"));
      return;
    }
    if (
      outcome === CALLBACK_REQUIRED &&
      moment(nextAttemptAfter).isBefore(moment().add(4, "hours"))
    ) {
      showError(t("offerFollowupAgent.errors.callbackTime"));
      return;
    }
    try {
      setIsSaving(true);
      const payload = {
        decision: toOfferFollowupDecision(outcome),
        notes: agentNotes.trim(),
        nextAttemptAfter:
          outcome === CALLBACK_REQUIRED ? nextAttemptAfter : undefined
      };
      await completeOfferFollowupLead(entry._id, payload);
      showSuccess(t("offerFollowupAgent.saved"));
      await loadNextLead();
    } catch (error) {
      console.log("Error in completing offer follow-up", error);
      showError(
        error?.response?.data?.message ||
          t("offerFollowupAgent.errors.saveOutcome")
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadNextLead();
  }, [loadNextLead]);

  useEffect(() => {
    clearInterval(timer.current);
    const updateOfferFollowupAgentTimestampHandler = async () => {
      try {
        await validateAndUpdateOfferFollowupAgentInfo(entry._id, {
          shouldAssignAgent: false
        });
      } catch (error) {
        console.log(error);
        if (error.response?.data?.data?.isAlreadyTaken) {
          showError(tRef.current("leadAgent.alreadyTakenMsg"));
          loadNextLead();
        } else {
          showError(error.response?.data?.message);
        }
      }
    };
    if (entry?._id && (assignedVia === SEARCH_LEAD_BY.AUTO || isCallStarted)) {
      timer.current = setInterval(() => {
        updateOfferFollowupAgentTimestampHandler();
      }, LEAD_AGENT_TIMESTAMP_UPDATE_DELAY);
    } else {
      clearInterval(timer.current);
    }
    return () => clearInterval(timer.current);
  }, [assignedVia, entry?._id, isCallStarted, loadNextLead]);

  useEffect(
    () => () => {
      searchRequestId.current += 1;
      fetchSearchOptions.cancel();
      clearInterval(timer.current);
    },
    [fetchSearchOptions]
  );

  useEffect(() => {
    if (!entry?._id) return undefined;
    const shouldReleaseAssignment = assignedVia === SEARCH_LEAD_BY.AUTO;
    return registerAgentAssignment({
      key: `offer-followup-agent-${entry._id}`,
      route: "offer-followup-agent",
      isAssigned: shouldReleaseAssignment,
      isInCall: isCallStarted,
      releaseAssignment: () =>
        shouldReleaseAssignment ? releaseOfferFollowupLead(entry._id) : null
    });
  }, [assignedVia, entry?._id, isCallStarted, registerAgentAssignment]);

  useBlockInCallNavigation({
    when: isCallStarted,
    allowedPathPrefix: "/dashboard/offer-followup-agent",
    onBlocked: () => showError(t("agentRouteSwitch.activeCallError")),
    unloadMessage: t("agentRouteSwitch.activeCallError")
  });

  if (isLoading) return <Loader />;

  const searchControls = (
    <div className="d-flex justify-content-end my-3">
      <Col xs={12} sm={8} md={5} lg={4}>
        <InputGroup>
          <SplitButton
            id="search-offer-followup-lead"
            title={t(
              `offerFollowupAgent.searchTypes.${searchType.toLowerCase()}`
            )}
            variant="outline-secondary"
            disabled={isCallStarted}
          >
            {OFFER_FOLLOWUP_SEARCH_OPTIONS.map((type) => (
              <DropdownItem
                key={type}
                active={searchType === type}
                disabled={isCallStarted}
                onClick={() => {
                  if (isCallStarted) return;
                  searchRequestId.current += 1;
                  fetchSearchOptions.cancel();
                  setSearchType(type);
                  setSearchValue("");
                  setSearchOptions([]);
                  setIsSearching(false);
                }}
              >
                {t(`offerFollowupAgent.searchTypes.${type.toLowerCase()}`)}
              </DropdownItem>
            ))}
          </SplitButton>
          <SelectContainer
            isClearable
            className="flex-fill"
            styles={{
              control: (base) => ({
                ...base,
                borderRadius: "0 4px 4px 0",
                borderColor: "var(--field-border-color-regular)"
              }),
              menu: (base) => ({ ...base, zIndex: 1021 })
            }}
            placeholder={`${t("offerFollowupAgent.searchBy")}...`}
            isLoading={isSearching}
            isDisabled={isCallStarted}
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null
            }}
            getOptionLabel={(option) => {
              const lead = option.leadSnapshot || option;
              return searchType === SEARCH_LEAD_BY.PHONE
                ? `${lead.contactName || EMPTY_VALUE} (${lead.phone || EMPTY_VALUE})`
                : `${lead.email || EMPTY_VALUE} (${lead.phone || EMPTY_VALUE})`;
            }}
            getOptionValue={(option) => option._id}
            inputValue={searchValue}
            value={null}
            options={searchOptions}
            onInputChange={(value, { action }) =>
              handleSearchInputChange(value, action)
            }
            onChange={(option, { action }) => {
              if (action !== "select-option" || isCallStarted) return;
              setSearchValue("");
              setSearchOptions([]);
              loadNextLead(
                { offerFollowupId: option._id, searchType },
                searchType
              );
            }}
          />
        </InputGroup>
      </Col>
    </div>
  );

  if (!entry) {
    return (
      <>
        {searchControls}
        <div className="mt-5 text-center fs-4">
          <div>{t("offerFollowupAgent.emptyQueue")}</div>
          <Button className="mt-3" onClick={() => loadNextLead()}>
            <FontAwesomeIcon icon={faArrowRotateRight} className="me-2" />
            {t("offerFollowupAgent.refreshQueue")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      {searchControls}
      <CustomCard className="p-4 my-4">
        <Row>
          <Col md={12} lg={6}>
            <CustomDiv className="mb-4 position-relative">
              {assignedVia !== SEARCH_LEAD_BY.AUTO && (
                <AbsoluteInfoContainer>
                  <ExtraInfoCustomBadge className="position-absolute bg-primary text-white fw-bold text-center">
                    {t("offerFollowupAgent.searchedBy", {
                      type: t(
                        `offerFollowupAgent.searchTypes.${assignedVia.toLowerCase()}`
                      )
                    })}
                  </ExtraInfoCustomBadge>
                </AbsoluteInfoContainer>
              )}
              <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                <h3 className="fw-bold mb-0">{leadDetails.contactName}</h3>
                <CustomBadge
                  className="px-3 py-2 d-flex align-items-center fw-normal"
                  $btnbg={`--lead-badge-bg`}
                  $btncolor={`--lead-color`}
                >
                  {t("offerFollowupAgent.lead")}
                </CustomBadge>
                {isCallRestricted && (
                  <CustomBadge className="p-2 bg-light text-dark fw-normal border">
                    <FontAwesomeIcon
                      className="text-secondary me-2"
                      icon={faCalendarDays}
                    />
                    {t("offerFollowupAgent.nextAttemptAfter", {
                      date: generateDateTime(nextAllowedAttemptAt)
                    })}
                  </CustomBadge>
                )}
              </div>
              <div className="d-flex flex-wrap text-secondary">
                <div>ID: {entry._id || EMPTY_VALUE}</div>
                <DotSeparator />
                <div>
                  {t("offerFollowupAgent.createdAt", {
                    date: generateDateTime(leadDetails.createdAt)
                  })}
                </div>
                <DotSeparator />
                <div className="text-break">{leadDetails.email}</div>
                <DotSeparator />
                <div>{leadDetails.phone || EMPTY_VALUE}</div>
              </div>
            </CustomDiv>

            <CustomDiv className="mb-4 py-4">
              <Row>
                <Col md={6} className="border-end">
                  <CustomFontSizeDiv
                    $fs="12"
                    className="text-secondary fw-bold"
                  >
                    {t("offerFollowupAgent.company").toUpperCase()}
                  </CustomFontSizeDiv>
                  <CustomFontSizeDiv $fs="18" className="fw-bold mb-3">
                    {leadDetails.companyName}
                  </CustomFontSizeDiv>
                  {leadDetails.tenantName !== EMPTY_VALUE &&
                    leadDetails.tenantName !== leadDetails.companyName && (
                      <div className="text-secondary small mb-3">
                        {t("offerFollowupAgent.tenant")}:{" "}
                        {leadDetails.tenantName}
                      </div>
                    )}
                  <CustomFontSizeDiv
                    $fs="12"
                    className="text-secondary fw-bold"
                  >
                    {t("offerFollowupAgent.offerSentAt").toUpperCase()}
                  </CustomFontSizeDiv>
                  <CustomFontSizeDiv $fs="18" className="fw-bold">
                    {generateDateTime(leadDetails.offerSentAt)}
                    {offerSentDaysAgo !== null && (
                      <span className="ms-2 text-secondary fs-6">
                        {offerSentDaysAgo === 0
                          ? t("offerFollowupAgent.today")
                          : t("offerFollowupAgent.daysAgo", {
                              count: offerSentDaysAgo
                            })}
                      </span>
                    )}
                  </CustomFontSizeDiv>
                </Col>
                <Col md={6} className="py-1 px-3">
                  <CustomFontSizeDiv
                    $fs="12"
                    className="text-secondary fw-bold"
                  >
                    {t("offerFollowupAgent.product").toUpperCase()}
                  </CustomFontSizeDiv>
                  <CustomFontSizeDiv $fs="18" className="fw-bold mb-3">
                    {leadDetails.service}
                  </CustomFontSizeDiv>
                  <CustomFontSizeDiv
                    $fs="12"
                    className="text-secondary fw-bold"
                  >
                    {t("offerFollowupAgent.offerVolume").toUpperCase()}
                  </CustomFontSizeDiv>
                  <CustomFontSizeDiv $fs="18" className="fw-bold">
                    {leadDetails.offerValue}
                  </CustomFontSizeDiv>
                </Col>
              </Row>
            </CustomDiv>

            <ActionBlock className="mb-4">
              <Row className="align-items-center">
                <Col md={6}>
                  {!isCallStarted ? (
                    <div className="text-center">
                      {isCallRestricted && (
                        <div className="mb-2 small text-secondary">
                          {t("offerFollowupAgent.callRestrictedNote", {
                            date: generateDateTime(nextAllowedAttemptAt)
                          })}
                        </div>
                      )}
                      {isSaving ? (
                        <SpinnerButton
                          btnText={t("offerFollowupAgent.calling")}
                          btnStyle="btn-success text-white"
                        />
                      ) : (
                        <CallContactButton
                          variant="success"
                          className="text-start"
                          disabled={!leadDetails.phone || isCallRestricted}
                          onClick={handleStartCall}
                        >
                          <FontAwesomeIcon
                            icon={faPhone}
                            className="ms-1 me-2"
                          />
                          {t("offerFollowupAgent.callContact")}
                        </CallContactButton>
                      )}
                      <a
                        hidden
                        ref={phoneNumberClickRef}
                        href={`tel:${leadDetails.phone}`}
                      >
                        <></>
                      </a>
                    </div>
                  ) : (
                    <Row>
                      {OUTCOME_ACTIONS.map(
                        ({
                          outcome: actionOutcome,
                          labelKey,
                          icon,
                          activeVariant,
                          inactiveVariant
                        }) => {
                          const isActive = outcome === actionOutcome;
                          const handleOutcomeClick = () => {
                            if (actionOutcome === CALLBACK_REQUIRED) {
                              setShowContactLaterDialog(true);
                              return;
                            }
                            setOutcome(actionOutcome);
                          };
                          return (
                            <Col
                              className="pe-0"
                              sm={12}
                              md={6}
                              key={actionOutcome}
                            >
                              <OutcomeButton
                                className={`text-start mb-2 w-100 ${
                                  isActive ? "is-selected" : ""
                                }`}
                                variant={
                                  isActive ? activeVariant : inactiveVariant
                                }
                                active={isActive}
                                aria-pressed={isActive}
                                disabled={isSaving}
                                onClick={handleOutcomeClick}
                              >
                                <FontAwesomeIcon
                                  icon={icon}
                                  className="ms-1 me-2"
                                />
                                {t(`offerFollowupAgent.${labelKey}`)}
                              </OutcomeButton>
                            </Col>
                          );
                        }
                      )}
                      {outcome === CALLBACK_REQUIRED && (
                        <Col xs={12} className="text-secondary small mt-1">
                          {t("offerFollowupAgent.selectedCallbackDate", {
                            date: generateDateTime(nextAttemptAfter)
                          })}
                        </Col>
                      )}
                    </Row>
                  )}
                </Col>
                <Col md={6} className="mt-3 mt-md-0">
                  <CustomFontSizeDiv
                    $fs="12"
                    className="text-secondary fw-bold"
                  >
                    {t("offerFollowupAgent.notes").toUpperCase()}
                  </CustomFontSizeDiv>
                  <NotesTextArea
                    as="textarea"
                    className="p-3 w-100 mt-1 rounded"
                    onChange={({ target }) => setAgentNotes(target.value)}
                    placeholder={t("offerFollowupAgent.notesPlaceholder")}
                    value={agentNotes}
                    disabled={!isCallStarted || isSaving}
                  />
                  {isCallStarted && (
                    <div className="d-flex justify-content-between mt-1 text-secondary small">
                      {t("offerFollowupAgent.minimumCharacters", {
                        count: agentNotes.trim().length
                      })}
                    </div>
                  )}
                </Col>
              </Row>
              {isCallStarted && (
                <div className="text-end mt-3">
                  <AgentViewActivityBtn
                    variant="outline-primary"
                    disabled={isSaving}
                    onClick={handleComplete}
                  >
                    {t("offerFollowupAgent.saveAndComplete")}
                  </AgentViewActivityBtn>
                </div>
              )}
            </ActionBlock>

            <CustomDiv>
              <div className="bg-white p-3">
                <h5 className="fw-bold mb-4">
                  <FontAwesomeIcon className="mx-2" icon={faClockRotateLeft} />
                  {t("offerFollowupAgent.contactHistory")}
                </h5>
                <ContactHistoryScroll>
                  {history.length ? (
                    history.map((item, index) => (
                      <HistoryItem key={item._id}>
                        <HistoryIcon>
                          <FontAwesomeIcon icon={faCheck} />
                        </HistoryIcon>
                        <CustomFontSizeDiv $fs="18" className="text-secondary">
                          {getStageDisplayName(item)}
                        </CustomFontSizeDiv>
                        <div className="text-secondary">
                          {generateDateTime(item.createdAt)}
                        </div>
                        <div className="text-secondary">
                          {t("offerFollowupAgent.createdBy")}{" "}
                          {item.createdBy?.email || EMPTY_VALUE}
                        </div>
                        <div className="fw-bold text-break">
                          {item.notice || EMPTY_VALUE}
                        </div>
                      </HistoryItem>
                    ))
                  ) : (
                    <Alert variant="secondary" className="mb-0">
                      {t("offerFollowupAgent.noContactHistory")}
                    </Alert>
                  )}
                </ContactHistoryScroll>
              </div>
            </CustomDiv>
          </Col>

          <Col md={12} lg={2} className="mt-4 mt-lg-0">
            <CustomCard>
              <ControlledHeightContainer>
                <CustomCardHeader className="sticky-top bg-light fw-bold">
                  {t("offerFollowupAgent.activities")}
                </CustomCardHeader>
                <div className="p-3">
                  <OfferActivities attempts={entry?.attempts} />
                </div>
              </ControlledHeightContainer>
            </CustomCard>
          </Col>

          <Col md={12} lg={4} className="mt-4 mt-lg-0">
            <CustomCard>
              <ControlledHeightContainer>
                <CustomCardHeader className="sticky-top bg-light fw-bold">
                  {t("offerFollowupAgent.script")}
                </CustomCardHeader>
                <div className="p-2">
                  <PDFViewer
                    objHeight="650"
                    ariaLabel="offer-followup-ls"
                    scriptUrl={generateScriptUrl({
                      workspace: user.workspace,
                      scriptProp: SCRIPT_WORKSPACE_PATH.FOLLOWUP,
                      localFileName: SCRIPT_LOCAL_FILE_NAME.FOLLOWUP
                    })}
                  />
                </div>
              </ControlledHeightContainer>
            </CustomCard>
          </Col>
        </Row>
      </CustomCard>
      <PickerDialog
        show={showContactLaterDialog}
        onHide={() => setShowContactLaterDialog(false)}
        activityHandler={handleContactLaterSelected}
        minimumHoursFromNow={4}
        minimumDateError={t("offerFollowupAgent.errors.callbackTime")}
      />
    </>
  );
};

export default OfferFollowupAgentPanelView;
